import { ArrowRightLeft, FileText, TrendingUp } from 'lucide-react';
import React from 'react';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';

import type { IInvoiceSaleFullCanEdit } from '../../api/saleToIncome.interface';
import { FormDialog } from './FormDialog';

type Props = {
  setInvoiceData: React.Dispatch<
    React.SetStateAction<IInvoiceSaleFullCanEdit | null>
  >;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  loading: boolean;
};

function ReportHeader({ setInvoiceData, setLoading }: Props) {
  return (
    <div className="relative overflow-hidden">
      {/* Background with animated gradient */}
      <div className="absolute inset-0 bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900"></div>

      {/* Animated background pattern */}
      <div className="absolute inset-0 opacity-5 dark:opacity-10">
        <div className="absolute -right-40 -top-40 h-80 w-80 rounded-full bg-gradient-to-br from-blue-400 to-purple-600 blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 h-80 w-80 rounded-full bg-gradient-to-tr from-indigo-400 to-pink-600 blur-3xl"></div>
      </div>

      {/* Main content */}
      <div className="relative border-b border-slate-200/50 bg-white/80 backdrop-blur-sm dark:border-slate-700/50 dark:bg-slate-900/80">
        <div className="px-6 py-6">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            {/* Left Section - Header Info */}
            <div className="flex items-start gap-4">
              {/* Icon Container */}
              <div className="relative">
                {/* Background glow */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 opacity-20 blur-lg"></div>

                {/* Main icon container */}
                <div className="relative flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-600 shadow-lg">
                  <ArrowRightLeft className="h-8 w-8 text-white" />
                </div>

                {/* Floating accent icons */}
                <div className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500 shadow-md">
                  <TrendingUp className="h-3 w-3 text-white" />
                </div>
                <div className="absolute -bottom-2 -left-2 flex h-6 w-6 items-center justify-center rounded-full bg-amber-500 shadow-md">
                  <FileText className="h-3 w-3 text-white" />
                </div>
              </div>

              {/* Text Content */}
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                    تحويل فاتورة بيع الي طلبية وارد
                  </h1>
                  <Badge
                    variant="outline"
                    className="border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-700 dark:bg-blue-950/30 dark:text-blue-300"
                  >
                    تحويل
                  </Badge>
                </div>

                <p className="max-w-2xl text-slate-600 dark:text-slate-400">
                  الهدف هو تحويل فاتورة بيع الي طلبية وارد ومن ثم فاتورة وارد.
                  يمكنك اختيار فاتورة البيع وتحويلها إلى طلبية وارد جديدة.
                </p>

                {/* Feature highlights */}
                <div className="flex flex-wrap items-center gap-4 pt-2">
                  <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                    <div className="h-2 w-2 rounded-full bg-blue-500"></div>
                    <span>تحويل سريع</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                    <div className="h-2 w-2 rounded-full bg-emerald-500"></div>
                    <span>حفظ البيانات</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                    <div className="h-2 w-2 rounded-full bg-purple-500"></div>
                    <span>تتبع العملية</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Section - Action Card */}
            <div className="flex flex-col gap-4 lg:items-end">
              {/* Stats Cards */}
              <div className="flex gap-3">
                <Card className="border-slate-200 bg-white/60 backdrop-blur-sm dark:border-slate-700 dark:bg-slate-800/60">
                  <CardContent className="p-3">
                    <div className="text-center">
                      <div className="text-lg font-bold text-blue-600 dark:text-blue-400">
                        0
                      </div>
                      <div className="text-xs text-slate-600 dark:text-slate-400">
                        تم التحويل
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-slate-200 bg-white/60 backdrop-blur-sm dark:border-slate-700 dark:bg-slate-800/60">
                  <CardContent className="p-3">
                    <div className="text-center">
                      <div className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                        0
                      </div>
                      <div className="text-xs text-slate-600 dark:text-slate-400">
                        في الانتظار
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Action Button */}
              <div className="flex items-center gap-3">
                <Separator
                  orientation="vertical"
                  className="h-8 bg-slate-300 dark:bg-slate-600"
                />
                <FormDialog
                  setInvoiceData={setInvoiceData}
                  setLoading={setLoading}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ReportHeader;
